import type { FeatureKey } from '@shared/config';
import type { UserSummary } from '@shared/types';
import { useQuery } from '@tanstack/react-query';
import { checkEntitlement, type EntitlementDecision } from '@/domain/entitlements';
import { useAuth } from '@/app/auth-context';
import { getAccountEntitlements, type AccountEntitlements } from '@/lib/api';
import { queryKeys } from '@/lib/query';

export interface FeatureEntitlement {
  decision: EntitlementDecision;
  isLoading: boolean;
  /** The server remains authoritative if configuration cannot be refreshed. */
  isUnavailable: boolean;
}

export function resolveFeatureEntitlement(
  user: Pick<UserSummary, 'plan'> | null,
  billingEnabled: boolean,
  feature: FeatureKey,
): EntitlementDecision {
  return checkEntitlement({ plan: user?.plan ?? 'free', billingEnabled }, feature);
}

export function resolveServerFeatureEntitlement(
  user: Pick<UserSummary, 'plan'> | null,
  entitlements: AccountEntitlements,
  feature: FeatureKey,
): EntitlementDecision {
  const derived = resolveFeatureEntitlement(user, entitlements.billingEnabled, feature);
  const allowed = entitlements.features[feature];
  if (allowed === derived.allowed) return derived;
  return allowed ? { allowed: true, reason: 'granted' } : { allowed: false, reason: 'revoked' };
}

/**
 * A single client-side presentation gate for plan-aware controls. The Worker
 * independently enforces every protected action; this only prevents a
 * confusing request that is known to be unavailable.
 */
export function useFeatureEntitlement(feature: FeatureKey): FeatureEntitlement {
  const { user } = useAuth();
  const entitlements = useQuery({
    queryKey: queryKeys.entitlements(user?.id ?? 'anonymous'),
    queryFn: getAccountEntitlements,
    enabled: user !== null,
    staleTime: 2 * 60_000,
  });
  const fallback = resolveFeatureEntitlement(user, false, feature);
  return {
    decision: entitlements.data
      ? resolveServerFeatureEntitlement(user, entitlements.data, feature)
      : fallback,
    isLoading: entitlements.isPending,
    isUnavailable: entitlements.isError || entitlements.data === undefined,
  };
}
